import { createFileRoute, Link } from "@tanstack/react-router";
import { PublicFooter, PublicHeader } from "@/components/public-layout";
import { PRIVACY_EMAIL, SUPPORT_EMAIL } from "@/lib/constants";
import {
	buildLocalizedSeoLinks,
	getLocalizedSeoUrls,
	ogImageUrl,
} from "@/lib/seo";

export const Route = createFileRoute("/privacy")({
	component: Privacy,
	head: ({ matches }) => {
		const seoUrls = getLocalizedSeoUrls({
			matches,
			internalPath: "/privacy",
		});

		return {
			meta: [
				{
					title: "AgentReacher | Privacy Policy",
				},
				{
					name: "description",
					content:
						"Learn how AgentReacher protects your privacy. We explain what data we collect, how we use it, and your rights when using our platform.",
				},
				{
					property: "og:title",
					content: "AgentReacher | Privacy Policy",
				},
				{
					property: "og:description",
					content:
						"How AgentReacher protects your privacy and handles your data.",
				},
				{
					property: "og:image",
					content: ogImageUrl("privacy"),
				},
				{ property: "og:url", content: seoUrls.canonicalUrl },
				{ property: "og:type", content: "website" },
				{ name: "twitter:card", content: "summary_large_image" },
				{ name: "twitter:title", content: "AgentReacher | Privacy Policy" },
				{
					name: "twitter:description",
					content:
						"How AgentReacher protects your privacy and handles your data.",
				},
				{ name: "twitter:image", content: ogImageUrl("privacy") },
			],
			links: buildLocalizedSeoLinks({
				matches,
				internalPath: "/privacy",
			}),
		};
	},
});

function Privacy() {
	const lastUpdated = "May 16, 2026";

	const sections = [
		{
			title: "Information We Collect",
			content: [
				{
					subtitle: "Account Information",
					text: "When you create an account, we collect your email address, name, and any profile information you choose to provide.",
				},
				{
					subtitle: "Content and Brand Assets",
					text: "We store the content and campaigns your AI agents create, including brand assets, images, copy variations, and any media you upload.",
				},
				{
					subtitle: "Posts, Schedules, and Publishing Records",
					text: "We store post drafts, captions, selected platforms, scheduled publishing times, approval state, delivery status, platform response details, and error messages needed to operate your content calendar and troubleshoot publishing issues.",
				},
				{
					subtitle: "Connected Accounts",
					text: "When you connect social media or publishing channels, we store the authentication tokens, app passwords where required, and channel metadata needed to publish on your behalf.",
				},
				{
					subtitle: "Google and YouTube Data",
					text: "If you connect a YouTube channel, AgentReacher requests access to the YouTube Data API scopes you approve, including youtube.upload so we can upload videos and Shorts you choose to publish, and youtube.readonly so we can read basic channel information such as channel ID, channel name, custom URL, thumbnail, and subscriber count. We store OAuth access tokens, refresh tokens, token expiration times, and the connected YouTube channel metadata needed to keep the integration working.",
				},
				{
					subtitle: "Usage Data",
					text: "We collect information about how you use AgentReacher, including pages visited, features used, agent activity, and campaign performance metrics.",
				},
				{
					subtitle: "AI Assistant and ChatGPT App Activity",
					text: "When you use AgentReacher through an AI assistant, MCP client, or ChatGPT app, we receive the specific tool requests and resources needed to complete your request, such as a post caption, selected targets, workspace, media reference, or scheduling instruction. We do not request or reconstruct your full chat history.",
				},
				{
					subtitle: "Device Information",
					text: "We may collect information about the device you use to access AgentReacher, including browser type, operating system, and IP address.",
				},
			],
		},
		{
			title: "How We Use Your Information",
			content: [
				{
					subtitle: "Providing Our Services",
					text: "We use your information to connect your AI to social media, generate and publish content, schedule posts, and provide the core functionality of our platform.",
				},
				{
					subtitle: "YouTube Publishing",
					text: "For YouTube, we use Google user data only to connect your channel, display the connected channel in AgentReacher, refresh access when needed, create drafts in AgentReacher, and upload or schedule videos and Shorts that you explicitly create, schedule, or publish through the product or API. We do not use Google user data for advertising, sell it, or transfer it except as needed to provide and secure the integration.",
				},
				{
					subtitle: "Improving AgentReacher",
					text: "Usage data helps us understand how brands use AgentReacher so we can improve content generation, agent performance, and develop new features.",
				},
				{
					subtitle: "Communication",
					text: "We may send you service-related emails, updates about new features, and occasional marketing communications, which you can opt out of at any time.",
				},
				{
					subtitle: "Security",
					text: "We use your information to detect and prevent fraud, abuse, and other harmful activities on our platform.",
				},
				{
					subtitle: "Support",
					text: "We use support messages, contact details, account context, and relevant diagnostic information to answer questions, investigate bugs, and resolve account or publishing issues.",
				},
			],
		},
		{
			title: "How We Protect Your Data",
			content: [
				{
					subtitle: "Encryption",
					text: "All data transmitted between your device and our servers is encrypted using industry-standard TLS encryption.",
				},
				{
					subtitle: "OAuth Tokens and App Passwords",
					text: "Connected-account credentials are used only to keep your publishing workflows working, refresh access when a platform requires it, and publish or manage posts you request through AgentReacher.",
				},
				{
					subtitle: "Google User Data Handling",
					text: "Google OAuth tokens are stored with access controls and are used only by AgentReacher server-side systems to perform the YouTube actions you authorize. We do not expose Google OAuth tokens in the browser, to other workspace members, or to third parties. Disconnecting a YouTube channel removes the stored Google tokens and connected-channel record from AgentReacher.",
				},
				{
					subtitle: "Secure Storage",
					text: "Your data is stored on secure servers with access controls. Disconnecting a social account removes the stored publishing access for that account.",
				},
				{
					subtitle: "Tool and App Boundaries",
					text: "AgentReacher tools act only on the workspace and connected accounts authorized for your account. Tool responses are limited to information relevant to the requested action, such as connected channels, draft details, schedule status, media references, or publication results.",
				},
				{
					subtitle: "Access Controls",
					text: "Only authorized team members have access to user data, and only when necessary for providing support or maintaining the service.",
				},
				{
					subtitle: "Regular Updates",
					text: "We regularly update our security practices and infrastructure to protect against new threats.",
				},
			],
		},
		{
			title: "Data Sharing and Third Parties",
			content: [
				{
					subtitle: "We Don't Sell Your Data",
					text: "We never sell your personal information, social account tokens, app passwords, posts, or brand assets to third parties.",
				},
				{
					subtitle: "Service Providers",
					text: "We work with trusted service providers who help us operate AgentReacher, including hosting, database storage, file storage, email delivery, payments, analytics, error monitoring, and customer support. These providers are authorized to process data only as needed to provide services to AgentReacher.",
				},
				{
					subtitle: "Social Platforms",
					text: "When you ask AgentReacher to connect, schedule, publish, update, or delete content on a social platform, we send the required content, media, account identifiers, and publishing settings to that platform's API.",
				},
				{
					subtitle: "Google Data Sharing",
					text: "We do not sell, rent, or share Google user data with advertisers or data brokers. We do not transfer Google user data to third parties except to service providers acting on our behalf, when required for security or legal compliance, or when you direct AgentReacher to publish content to YouTube.",
				},
				{
					subtitle: "Legal Requirements",
					text: "We may disclose information if required by law, such as in response to a valid legal request from law enforcement.",
				},
				{
					subtitle: "Business Transfers",
					text: "If AgentReacher is involved in a merger or acquisition, your information may be transferred as part of that transaction.",
				},
			],
		},
		{
			title: "Data Retention and Deletion",
			content: [
				{
					subtitle: "Account Data",
					text: "We retain account, workspace, billing, team, and support records while your account is active and as needed afterward for legal, security, accounting, and dispute-resolution purposes.",
				},
				{
					subtitle: "Connected YouTube Channels",
					text: "We retain connected YouTube channel metadata and OAuth tokens while your YouTube channel remains connected to AgentReacher. You can disconnect the channel at any time from the dashboard, which deletes the stored YouTube tokens and connected-channel record from our active systems.",
				},
				{
					subtitle: "Posts, Drafts, and Media",
					text: "We retain posts, drafts, schedules, publication records, and uploaded media so you can manage your content calendar and publishing history. You can delete individual posts and media from AgentReacher, or request account deletion to remove your account data.",
				},
				{
					subtitle: "Tool Logs and Diagnostics",
					text: "We keep MCP, ChatGPT app, and API tool logs for troubleshooting, abuse prevention, security, and reliability. These logs are retained only as long as needed for those purposes unless a longer period is required by law or to resolve a dispute.",
				},
				{
					subtitle: "Backups",
					text: "Deleted data may remain in encrypted backups for a limited period before those backups expire through our normal backup rotation. We do not restore deleted data to active systems except when needed for security, legal, or disaster-recovery purposes.",
				},
				{
					subtitle: "Policy Updates",
					text: "If we change how AgentReacher accesses, uses, stores, or shares Google user data, we will update this policy and notify users when the change is material.",
				},
			],
		},
		{
			title: "User Controls",
			content: [
				{
					subtitle: "Connected Accounts",
					text: "You can disconnect linked social accounts from AgentReacher. Disconnecting removes the stored publishing credentials for that account and stops future AgentReacher publishing to that channel.",
				},
				{
					subtitle: "Posts and Media",
					text: "You can delete posts, drafts, schedules, and uploaded media from your workspace. Deleting a record in AgentReacher does not always remove content that has already been published on an external social platform; use the platform's own controls when needed.",
				},
				{
					subtitle: "Workspace Access",
					text: "Workspace owners and admins can manage workspace members and invitations. Removing a member stops their future access to the workspace in AgentReacher.",
				},
				{
					subtitle: "Account Requests",
					text: "You can request access, correction, export, or deletion of personal data by contacting privacy support. We may need to verify your identity before completing the request.",
				},
			],
		},
		{
			title: "Google API Limited Use Disclosure",
			content: [
				{
					subtitle: "Limited Use",
					text: "AgentReacher's use and transfer of information received from Google APIs will adhere to the Google API Services User Data Policy, including the Limited Use requirements.",
				},
			],
		},
	];

	const rights = [
		"Access and download your data",
		"Correct inaccurate information",
		"Delete your account and data",
		"Opt out of marketing communications",
		"Export your campaigns and content",
		"Disconnect linked social accounts at any time",
		"Request information about data processing",
	];

	return (
		<div className="flex min-h-svh flex-col bg-background text-foreground">
			<PublicHeader />
			<main className="relative z-10 flex-1">
				<div className="mx-auto max-w-6xl px-4 sm:px-6">
					<div className="marketing-line-stack flex flex-col overflow-hidden divide-y divide-border">
						<div className="rounded-md bg-white dark:bg-black px-6 py-10 sm:px-8 sm:py-14">
							<div className="mx-auto max-w-3xl space-y-8">
								<div className="space-y-2">
									<h1 className="text-2xl font-medium tracking-tight">
										Privacy Policy
									</h1>
									<p className="text-sm text-muted-foreground">
										Last updated: {lastUpdated}
									</p>
								</div>

								<div className="space-y-8">
									{sections.map((section) => (
										<section key={section.title} className="space-y-3">
											<h2 className="text-base font-medium text-foreground">
												{section.title}
											</h2>
											<div className="space-y-4 text-sm leading-relaxed text-muted-foreground">
												{section.content.map((item) => (
													<div key={item.subtitle}>
														<h3 className="font-medium text-foreground mb-1">
															{item.subtitle}
														</h3>
														<p>{item.text}</p>
													</div>
												))}
											</div>
										</section>
									))}

									<section className="space-y-3">
										<h2 className="text-base font-medium text-foreground">
											Your Rights
										</h2>
										<div className="space-y-3 text-sm leading-relaxed text-muted-foreground">
											<p>
												You have the following rights regarding your personal
												data:
											</p>
											<ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground">
												{rights.map((right) => (
													<li key={right}>{right}</li>
												))}
											</ul>
										</div>
									</section>

									<section className="space-y-3">
										<h2 className="text-base font-medium text-foreground">
											Cookies
										</h2>
										<div className="space-y-3 text-sm leading-relaxed text-muted-foreground">
											<p>
												We use cookies to provide essential functionality and
												improve your experience. These include:
											</p>
											<ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground">
												<li>
													<strong className="text-foreground">
														Essential cookies:
													</strong>{" "}
													Required for the site to function properly, including
													authentication and security.
												</li>
												<li>
													<strong className="text-foreground">
														Measurement cookies:
													</strong>{" "}
													Help us understand how you use AgentReacher so we can
													improve.
												</li>
												<li>
													<strong className="text-foreground">
														Preference cookies:
													</strong>{" "}
													Remember your settings and preferences.
												</li>
											</ul>
										</div>
									</section>

									<section className="space-y-3">
										<h2 className="text-base font-medium text-foreground">
											Questions About Privacy?
										</h2>
										<div className="space-y-3 text-sm leading-relaxed text-muted-foreground">
											<p>
												If you have any questions about this privacy policy or
												how we handle your data, please contact us:
											</p>
											<p>
												For product support, account access, or connection
												problems, email{" "}
												<a
													href={`mailto:${SUPPORT_EMAIL}`}
													className="text-primary hover:text-primary/80 font-medium transition-colors"
												>
													{SUPPORT_EMAIL}
												</a>
												.
											</p>
											<p>
												<a
													href={`mailto:${PRIVACY_EMAIL}`}
													className="text-primary hover:text-primary/80 font-medium transition-colors"
												>
													{PRIVACY_EMAIL}
												</a>
											</p>
										</div>
									</section>
								</div>

								<Link
									to="/"
									className="inline-block text-sm text-muted-foreground transition-colors hover:text-foreground"
								>
									&larr; Back to home
								</Link>
							</div>
						</div>
					</div>
				</div>
			</main>
			<PublicFooter />
		</div>
	);
}
